import React, { useEffect, useMemo, useState } from "react";
import { useColorMode } from "@chakra-ui/react";
import { getThemeClasses } from "../utils/themeUtils";
import { FaTrash, FaArrowLeft, FaArrowRight } from "react-icons/fa";
import { IconWrapper } from "./IconWrapper";

// Define Project Type
interface Project {
  name: string;
  channels: string[];
}

// Props for ProjectForm
interface ProjectFormProps {
  isProjectSaving: boolean;
  newProject: Omit<Project, "id">;
  setNewProject: React.Dispatch<React.SetStateAction<Omit<Project, "id">>>;
  channelInputs: string[];
  handleAddChannelInput: () => void;
  handleChannelInputChange: (index: number, value: string) => void;
  handleRemoveChannelInput: (index: number) => void;
  handleAddProject: () => void;
  textColor: string;
  cardBg: string;
  isSidebarOpen: boolean;
}

const PAGE_SIZE = 3; // Number of channels per page

const ProjectForm: React.FC<ProjectFormProps> = ({
  newProject,
  isProjectSaving,
  setNewProject,
  channelInputs,
  handleAddChannelInput,
  handleChannelInputChange,
  handleRemoveChannelInput,
  handleAddProject,
  textColor,
  cardBg,
  isSidebarOpen,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const { colorMode } = useColorMode();
  const isDark = colorMode === "dark";
  const theme = getThemeClasses(isDark);

  // Pagination for channels
  const totalPages = Math.ceil(channelInputs.length / PAGE_SIZE);
  const paginatedChannels = channelInputs.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  // If channels shrink and current page no longer exists, reset to page 1
  useEffect(() => {
    if (currentPage > Math.max(totalPages, 1)) {
      setCurrentPage(1);
    }
  }, [totalPages, currentPage]);

  const textColorClass = useMemo(() => (isDark ? "text-white" : "text-slate-800"), [isDark]);
  const cardBgClass = useMemo(() => (isDark ? "bg-[#1A1C1F]" : "bg-white"), [isDark]);
  const cardBorderClass = useMemo(() => (isDark ? "border-[#2E2F33]" : "border-slate-200"), [isDark]);
  const inputBase = useMemo(
    () =>
      `${isDark ? "bg-[#0B0B0F] text-white placeholder-[#C3C9D3] border-[#2E2F33]" : "bg-white text-slate-800 placeholder-slate-400 border-slate-300"} w-full rounded-lg border px-3 py-2 outline-none ring-0 transition`,
    [isDark]
  );
  const inputFocus = useMemo(
    () => `focus:border-[#B59143] focus:ring-2 focus:ring-[#B59143]/30`,
    []
  );

  return (
    <div className="mb-4 w-full max-w-4xl">
      <div className="flex flex-col gap-6 mb-4">
        {/* Project Name */}
        <div className={`flex-1 ${cardBgClass} border ${cardBorderClass} rounded-xl p-4 shadow-sm`}>
          <label className={`block text-sm font-semibold mb-2 ${textColorClass}`}>
            Project Name
          </label>
          <input
            className={`${inputBase} ${inputFocus} hover:border-[#B59143]`
            }
            placeholder="Enter Project Name"
            value={newProject.name}
            onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
          />
        </div>

        {/* Channels */}
        <div className={`flex-1 ${cardBgClass} border ${cardBorderClass} rounded-xl p-4 shadow-sm`}>
          <div className="flex items-center justify-between">
            <div className={`text-sm font-semibold ${textColorClass}`}>Channels</div>
            <button
              type="button"
              onClick={handleAddChannelInput}
              className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-sm font-semibold ${theme.goldOutlineButton}`}
            >
              + Add Channel
            </button>
          </div>

          {/* Dynamic Channel Inputs with Pagination */}
          <div className="mt-3 space-y-2">
            {paginatedChannels.map((input, index) => (
              <div key={index} className="flex items-center gap-2">
                <input
                  className={`${inputBase} ${inputFocus} hover:border-[#B59143]`}
                  placeholder="Channel Name"
                  value={input}
                  onChange={(e) =>
                    handleChannelInputChange(
                      index + (currentPage - 1) * PAGE_SIZE,
                      e.target.value
                    )
                  }
                />
                <button
                  type="button"
                  aria-label="Remove Channel"
                  onClick={() =>
                    handleRemoveChannelInput(index + (currentPage - 1) * PAGE_SIZE)
                  }
                  className={`inline-flex items-center justify-center rounded-md p-2 ${isDark ? "border-[#2E2F33] bg-[#0B0B0F] text-[#C3C9D3] hover:bg-[#1A1C1F]" : "border-slate-200 bg-white text-slate-500 hover:bg-rose-50 hover:border-rose-200"} transition`}
                >
                  <IconWrapper icon={FaTrash} className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className={`mt-4 flex items-center justify-center gap-2 rounded-b-xl ${cardBgClass} py-2`}>
              <button
                type="button"
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className={`inline-flex h-9 w-9 items-center justify-center rounded-md border ${isDark ? "border-[#2E2F33] bg-[#0B0B0F] text-white enabled:hover:bg-[#1A1C1F]" : "border-slate-200 bg-white text-slate-600 enabled:hover:bg-slate-50 enabled:hover:border-slate-300"} disabled:opacity-40 disabled:cursor-not-allowed transition`}
                aria-label="Previous page"
              >
                <IconWrapper icon={FaArrowLeft} className="h-4 w-4" />
              </button>
              <span className="px-3 text-sm font-medium text-slate-600">{`${currentPage} / ${totalPages}`}</span>
              <button
                type="button"
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className={`inline-flex h-9 w-9 items-center justify-center rounded-md border ${isDark ? "border-[#2E2F33] bg-[#0B0B0F] text-white enabled:hover:bg-[#1A1C1F]" : "border-slate-200 bg-white text-slate-600 enabled:hover:bg-slate-50 enabled:hover:border-slate-300"} disabled:opacity-40 disabled:cursor-not-allowed transition`}
                aria-label="Next page"
              >
                <IconWrapper icon={FaArrowRight} className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Add Project */}
      <button
        type="button"
        onClick={handleAddProject}
        disabled={isProjectSaving || !newProject.name.trim()}
        className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold ${theme.primaryButton} disabled:opacity-60 disabled:cursor-not-allowed`}
      >
        {isProjectSaving && (
          <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
          </svg>
        )}
        {isProjectSaving ? "Adding..." : "Add Project"}
      </button>
    </div>
  );
};

export default ProjectForm;